import { useFormContext, useWatch } from 'react-hook-form'
import { Banner, Button, Form, Heading } from '@boltenergy-be/design-system'
import { ComplaintFlowInputs, ComplaintFlowProps } from '../types'
import { useState } from 'react'
import styles from 'pages/app/support/complaint-form/ComplaintForm.module.scss'
import FilePreview from 'components/form-fields/file-upload/FilePreview'
import { useTranslation } from 'react-i18next'
import DataBlock from 'features/contracts/add/components/data-block/DataBlock.tsx'
import boltBackend from 'lib/bolt-backend'
import { trackEvent } from 'utils/tracking.ts'
import { CreateComplaintCaseRequestBody } from '@boltenergy-be/shared'
import { useStoreSelector } from 'hooks/store.ts'
import { useGetLanguage } from 'hooks/use-get-language'
import { selectCurrentAccount } from 'store/contact/slice.ts'
import { log } from 'utils/logging.ts'
import { AxiosError } from 'axios'
import { Severity } from 'types/logging.ts'

const Overview = ({ setCurrentStep }: ComplaintFlowProps) => {
  // Redux
  const { contact } = useStoreSelector((store) => store.contact)
  const account = useStoreSelector(selectCurrentAccount)

  // i18n
  const { t } = useTranslation('support', { keyPrefix: 'complaintForm.overview' })
  const { t: tX } = useTranslation(['common', 'validation', 'errors'])
  const language = useGetLanguage()

  // Local state
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<'file' | 'submit' | 'catch' | null>(null)

  // React Hook Form
  const hookForm = useFormContext<ComplaintFlowInputs>()
  const { handleSubmit } = hookForm
  const formInputs = useWatch({ control: hookForm.control })

  /**
   * Submit handler for the contact step of the complaint flow.
   * @param {Omit<ComplaintFlowInputs, "complaint">} inputs
   */
  const submitComplaint = async (inputs: ComplaintFlowInputs) => {
    const startTime = performance.now()
    setLoading(true)
    setError(null)

    try {
      const attachmentFiles = inputs.attachments?.length ? Array.from(inputs.attachments) : []
      const totalSize = attachmentFiles.reduce((total, file) => total + (file?.size || 0), 0)

      // Upload files to S3
      const s3Res = await Promise.all(
        attachmentFiles?.length ? attachmentFiles.map(async (file) => await boltBackend.uploadPresigned(file)) : []
      )

      if (s3Res.some(({ success, data }) => !success || !data)) {
        setError('file')
        trackEvent(
          { category: 'complaint', object: 'files', action: 'fail' },
          { amount_of_files: attachmentFiles.length, total_attachment_size_bytes: totalSize }
        )
        log({
          error: 'Failed to presign complaint files',
          identifier: `[complaint:files]`,
          metaData: {
            complaintContext: {
              amount_of_files: attachmentFiles.length,
              total_attachment_size_bytes: totalSize,
              s3Res
            }
          }
        })
        return
      }

      // Create complaint case
      const attachmentKeys = s3Res.length ? s3Res.filter(({ data }) => !!data).map(({ data }) => data!) : []
      const body: CreateComplaintCaseRequestBody = {
        firstName: account.firstName,
        lastName: account.lastName,
        phone: inputs.phoneContactAllowed ? contact?.phone : undefined,
        email: contact?.email || '',
        customerNumber: account.customerNumber,
        language,
        subject: inputs.subject,
        description: inputs.description,
        attachmentKeys
      }
      const { success, data, ...errorData } = await boltBackend.postComplaintCase(body)

      if (!success) {
        setError('submit')
        trackEvent(
          { category: 'complaint', object: 'submit', action: 'fail' },
          { amount_of_files: attachmentFiles.length, total_attachment_size_bytes: totalSize }
        )
        log({
          error: 'Failed to submit complaint',
          identifier: `[complaint:submit]`,
          metaData: {
            complaintContext: {
              amount_of_files: attachmentFiles.length,
              total_attachment_size_bytes: totalSize,
              s3Res,
              ...errorData
            }
          }
        })
        return
      }

      if (!data?.caseId) {
        log({
          error: 'Complaint no case id returned',
          identifier: `[complaint:caseId]`,
          severity: Severity.INFO
        })
      }

      const totalDuration = performance.now() - startTime
      trackEvent(
        { category: 'complaint', object: 'submit', action: 'finish' },
        { duration_ms: totalDuration, amount_of_files: attachmentFiles.length, total_attachment_size_bytes: totalSize }
      )
      setCurrentStep('confirmation')
    } catch (e) {
      setError('catch')
      trackEvent({ category: 'complaint', action: 'fail' })
      log({
        error: 'Failed to submit complaint',
        identifier: `[complaint:catch]`,
        metaData: {
          complaintContext: {
            error: e instanceof AxiosError ? e.response : e
          }
        }
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <section>
      <header className="flex-col gap-200 mb-400">
        <Heading as="h2" variant="h4">
          {t(($) => $.title)}
        </Heading>
        <p>{t(($) => $.description)}</p>
      </header>

      <Form onSubmit={handleSubmit(submitComplaint)}>
        <section className="grid-col-full flex-col gap-400">
          <header className="flex-row gap-400">
            <Heading as="h2" variant="h4" serif>
              {t(($) => $.complaint.title)}
            </Heading>

            <Button representation="link" onClick={() => setCurrentStep('complaint')}>
              {tX(($) => $.change)}
            </Button>
          </header>

          <DataBlock label={t(($) => $.complaint.subject)}>{formInputs.subject}</DataBlock>
          <DataBlock label={t(($) => $.complaint.reason)}>{formInputs.description}</DataBlock>
          {!!formInputs.attachments?.length && (
            <DataBlock
              label={t(($) => $.complaint.attachment, {
                count: formInputs.attachments.length
              })}
            >
              <dl className={styles.attachments}>
                {Array.from(formInputs.attachments).map((attachment) => (
                  <FilePreview key={attachment.name} file={attachment} />
                ))}
              </dl>
            </DataBlock>
          )}
        </section>

        {!!error && (
          <Banner
            type="blocking"
            className="grid-col-full"
            title={tX(($) => $.default, {
              ns: 'errors'
            })}
          />
        )}

        <Form.Footer className="flex-row mt-400">
          <Button type="button" leadingIcon="arrowLeft" disabled={loading} variant="secondary" onClick={() => setCurrentStep('complaint')}>
            {tX(($) => $.previous)}
          </Button>

          <Button trailingIcon="arrowRight" loading={loading} className="ml-auto" disabled={!!error}>
            {tX(($) => $.send)}
          </Button>
        </Form.Footer>
      </Form>
    </section>
  )
}

export default Overview
